import React from "react";
import { Form, Select, DatePicker, Upload, Button } from "antd";
import type { FormInstance, UploadProps } from "antd";
import dayjs from "dayjs";
import { UploadCloud } from "lucide-react";
import { getPaymentMethodCode, PaymentMethod } from "../../Enums";
import { PaymentOption, PaymentOptionValue } from "../PaymentsOption";

interface ExpenseFormProps {
  form: FormInstance;
  categories: any[];
  subcategories: any[];
  paymentMethods: any[];
  bankAccounts: any[];
  categoryId?: string;
  paymentMethodCode?: string;
  amount: number;
  showBankAccount: boolean;
  uploadProps: UploadProps;
  onCategoryChange: (value: string) => void;
  onPaymentMethodChange: (value: string) => void;
  onManageCategories: () => void;
  paymentDetails: PaymentOptionValue;
  onPaymentDetailsChange: (val: PaymentOptionValue) => void;
}

export const ExpenseForm: React.FC<ExpenseFormProps> = ({
  form,
  categories,
  subcategories,
  paymentMethods,
  bankAccounts,
  categoryId,
  paymentMethodCode,
  amount,
  showBankAccount,
  uploadProps,
  onCategoryChange,
  onPaymentMethodChange,
  onManageCategories,
  paymentDetails,
  onPaymentDetailsChange,
}) => {
  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={{
        expense_date: dayjs(),
      }}
      className="flex flex-col gap-4 h-full"
    >
      {/* Expense Details */}
      <div className="bg-white px-4 border border-gray-100 rounded-xl py-3">
        <div className="flex justify-between items-center mb-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Expense Details
          </span>

          <Button
            type="link"
            size="small"
            className="p-0 h-auto text-xs font-semibold flex items-center gap-1"
            onClick={onManageCategories}
          >
            (Add New)
          </Button>
        </div>

        <div className="flex flex-col gap-3">
          <Form.Item
            name="category_id"
            label={
              <span className="text-[11px] font-semibold text-gray-600">
                Category
              </span>
            }
            rules={[
              {
                required: true,
                message: "Select an expense category.",
              },
            ]}
            className="m-0"
          >
            <Select
              placeholder="Select category"
              className="h-9 rounded-md text-xs"
              options={categories.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
              onChange={(val) => {
                onCategoryChange(val);

                form.setFieldsValue({
                  subcategory_id: undefined,
                });
              }}
            />
          </Form.Item>

          <Form.Item
            name="subcategory_id"
            label={
              <span className="text-[11px] font-semibold text-gray-600">
                Subcategory
              </span>
            }
            rules={
              subcategories.length > 0
                ? [
                    {
                      required: true,
                      message: "Select a subcategory.",
                    },
                  ]
                : []
            }
            className="m-0"
          >
            <Select
              placeholder={
                !categoryId
                  ? "Select a category first"
                  : subcategories.length === 0
                    ? "No subcategories available"
                    : "Select subcategory"
              }
              disabled={!categoryId || subcategories.length === 0}
              className="h-9 rounded-md text-xs"
              options={subcategories.map((s) => ({
                value: s.id,
                label: s.name,
              }))}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="expense_date"
            label={
              <span className="text-[11px] font-semibold text-gray-600">
                Expense Date
              </span>
            }
            rules={[
              {
                required: true,
                message: "Select a date.",
              },
            ]}
            className="m-0"
          >
            <DatePicker
              className="h-9 rounded-md text-xs w-full"
              format="DD MMM YYYY"
            />
          </Form.Item>
        </div>
      </div>

      {/* Payment Information */}
      <div className="bg-white p-4 border border-gray-100 rounded-xl">
        <div className="mb-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Payment Information
          </span>
        </div>

        <div className="flex flex-col gap-3">
          <Form.Item
            name="payment_method_id"
            label={
              <span className="text-[11px] font-semibold text-gray-600">
                Payment Method
              </span>
            }
            rules={[
              {
                required: true,
                message: "Select a payment method.",
              },
            ]}
            className="m-0"
          >
            <Select
              placeholder="Select method"
              className="h-9 rounded-md text-xs"
              options={paymentMethods.map((m) => ({
                value: m.id,
                label: m.name,
              }))}
              onChange={(val) => {
                onPaymentMethodChange(val);

                onPaymentDetailsChange({
                  amount: 0,
                  note: "",
                  bank_account_id: "",
                  transaction_no: "",
                  reference_no: "",
                  cheques: [],
                });
              }}
            />
          </Form.Item>

          {paymentMethodCode && (
            <div className="pt-2 border-t border-gray-100">
              <PaymentOption
                paymentMethodCode={paymentMethodCode}
                value={paymentDetails}
                onChange={onPaymentDetailsChange}
                bankAccounts={bankAccounts}
              />
            </div>
          )}
        </div>
      </div>

      {/* Attachments */}
      <div className="bg-white px-4 border border-gray-100 rounded-xl py-3">
        <div className="mb-3">
          <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
            Attachments
          </span>
        </div>

        <Form.Item className="m-0">
          <Upload.Dragger
            {...uploadProps}
            className="border-dashed border-gray-200 bg-gray-50 rounded-xl hover:border-blue-500 transition-colors p-4"
          >
            <div className="flex flex-col items-center justify-center gap-2">
              <UploadCloud className="w-8 h-8 text-gray-400" />

              <div className="text-center">
                <p className="text-xs font-medium text-gray-700">
                  Click or drag invoice/proof here
                </p>

                <p className="text-[10px] text-gray-400 mt-1">
                  Accepts JPG, PNG, PDF · Max 5MB per file
                </p>
              </div>
            </div>
          </Upload.Dragger>
        </Form.Item>
      </div>

      {/* Total */}
      <div className="bg-blue-50/50 p-4 border border-blue-100 rounded-xl flex justify-between items-center">
        <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">
          Total Expense
        </span>

        <span className="text-base font-extrabold text-blue-800">
          LKR{" "}
          {Number(amount).toLocaleString(undefined, {
            minimumFractionDigits: 2,
          })}
        </span>
      </div>
    </Form>
  );
};
